import { ApprovalInFlightEditRule } from '@risksmart-app/domain/src/types/consts/approval-in-flight-edit-rule';
import { ApprovalRuleType } from '@risksmart-app/domain/src/types/consts/approval-rule-type';
import { z } from 'zod';

/**
 * Zod input schemas for the approval-config mutations (RSP-6096).
 *
 * Kept in a PURE-ZOD leaf module (no server imports) so the web mutation hooks
 * can parse against the SAME schemas the router enforces — the GraphQL
 * `*_insert_input` variable types the settings modals build are wire-nullable
 * on every field, and `.parse()` both narrows them and surfaces the server's
 * validation messages client-side. Single source of truth: the router and the
 * web mapper can never drift.
 */

const approverTargetFields = {
  // User ids are Auth0-style text subjects (user.Id is text, e.g.
  // "auth0|…"), NOT uuids — only group ids are uuid-shaped. Matches the
  // data-layer schema (services/data-layer/src/schemas/approval.ts).
  UserId: z.string().min(1).nullish(),
  UserGroupId: z.string().uuid().nullish(),
  OwnerApprover: z.boolean().nullish(),
};

/**
 * Each approver targets EXACTLY ONE of: a user (`UserId`), a group
 * (`UserGroupId`), or the entity owner (`OwnerApprover === true`). The refine
 * uses `!= null` so explicit `null` values (sent by forms for unset fields)
 * count as "unset", and only `OwnerApprover === true` (not merely non-null)
 * counts as the owner target.
 */
const exactlyOneApproverTarget = (data: {
  UserId?: string | null;
  UserGroupId?: string | null;
  OwnerApprover?: boolean | null;
}) => {
  const targets = [
    data.UserId != null,
    data.UserGroupId != null,
    data.OwnerApprover === true,
  ].filter(Boolean);

  return targets.length === 1;
};

const exactlyOneApproverTargetMessage = {
  message:
    'Each approver must set exactly one of UserId, UserGroupId, or OwnerApprover',
};

export const approverInputSchema = z
  .object(approverTargetFields)
  .refine(exactlyOneApproverTarget, exactlyOneApproverTargetMessage);

export const levelInputSchema = z.object({
  Description: z.string(),
  SequenceOrder: z.number().int().nonnegative(),
  // Domain-enum constrained (not a free string): an unknown rule type would
  // otherwise persist and break every consumer that switches on it.
  ApprovalRuleType: z.nativeEnum(ApprovalRuleType),
  Approvers: z.array(approverInputSchema),
});

export const insertApprovalInputSchema = z.object({
  ParentId: z.string().uuid().nullable(),
  Workflow: z.string().min(1),
  InFlightEditRule: z.nativeEnum(ApprovalInFlightEditRule),
  Levels: z.array(levelInputSchema).min(1),
});

export const updateApprovalInputSchema = z.object({
  Id: z.string().uuid(),
  // No ParentId: re-parenting an approval is not a feature; the server gate
  // derives from the persisted row, so accepting a new parent here would be a
  // re-anchoring vector.
  Approval: z.object({
    Workflow: z.string().min(1),
    InFlightEditRule: z.nativeEnum(ApprovalInFlightEditRule),
  }),
  DeleteLevelIds: z.array(z.string().uuid()),
  DeleteApproverIds: z.array(z.string().uuid()),
  UpdateLevels: z.array(
    z.object({
      Id: z.string().uuid(),
      Description: z.string(),
      SequenceOrder: z.number().int().nonnegative(),
      ApprovalRuleType: z.nativeEnum(ApprovalRuleType),
    })
  ),
  InsertLevels: z.array(levelInputSchema),
  InsertApprovers: z.array(
    z
      .object({
        LevelId: z.string().uuid(),
        ...approverTargetFields,
      })
      .refine(exactlyOneApproverTarget, exactlyOneApproverTargetMessage)
  ),
});

export const deleteApprovalInputSchema = z.object({ Id: z.string().uuid() });
